import { type UserReference } from '@databases/types/confluence';
import {
  DatabaseFieldType,
  type RawDatabaseValue,
  USER_VALUE_CURRENT,
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { produce } from 'immer';
import { isEqual } from 'lodash-es';

import { serializeUserReference } from '../../confluence/utils';
import { cheerioIterator } from '../../lib/dom';
import { selectCurrentUserId } from '../../redux/context/selectors';
import { type CommonRootState } from '../../redux/redux';
import { loadUserById } from '../../redux/users/actions';
import { selectUserByIdOptional } from '../../redux/users/selectors';
import { matchUserPath } from '../../urls';
import { generateId } from '../../utils';
import type { FieldType } from '../field-types';

function getCorrectedValue(
  { value }: { value: RawDatabaseValue<DatabaseFieldType.USER> },
  state: CommonRootState | undefined,
  resolveCurrent: boolean
): RawDatabaseValue<DatabaseFieldType.USER> {
  if (state) {
    // Resolve user if selection is "current user"
    if (resolveCurrent && value === USER_VALUE_CURRENT) {
      const currentUserId = selectCurrentUserId(state);
      return currentUserId
        ? [
            {
              ...(selectUserByIdOptional(state, { accountId: currentUserId }) ?? {
                accountId: currentUserId,
              }),
              _id: generateId(),
            },
          ]
        : [];
    }

    // Update the user name if we happen to have the user in the store
    if (Array.isArray(value)) {
      return produce(value, (updatedValue) => {
        for (const v of updatedValue.filter(isNonEmptyUserValue)) {
          const user = selectUserByIdOptional(state, { accountId: v.accountId });
          if (user) {
            v.displayName = user.displayName;
          }
        }
      });
    }
  }
  return value;
}

function getStringRepresentation(
  context: { value: RawDatabaseValue<DatabaseFieldType.USER> },
  state: CommonRootState | undefined
): string {
  const value = getCorrectedValue(context, state, true);
  return (Array.isArray(value) ? value : [])
    .filter(isNonEmptyUserValue)
    .map((v) => v.displayName)
    .join('\n');
}

/**
 * Returns true if the given user value is not empty. Empty user values are added by the user through the UI
 * but have not been filled with a user reference yet.
 */
export function isNonEmptyUserValue(
  value: Exclude<RawDatabaseValue<DatabaseFieldType.USER>, string>[0]
): value is Exclude<RawDatabaseValue<DatabaseFieldType.USER>, string>[0] & UserReference {
  return 'accountId' in value;
}

const userFieldType: FieldType<DatabaseFieldType.USER> = {
  supportedFilterTypes: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.CONTAINS,
    DatabaseFilterType.EMPTY,
  ],

  isCsvImportSupported: false,

  renderExportView: (context, state) => {
    const value = getCorrectedValue(context, state, true);
    if (Array.isArray(value)) {
      return value
        .filter(isNonEmptyUserValue)
        .map((user) => serializeUserReference(user.accountId, true))
        .join('<br/>');
    } else {
      return '';
    }
  },

  parseFromHtml: (el) => {
    return [
      ...cheerioIterator(el.find('a[href],.confluence-userlink,[data-mention-id]')),
    ].flatMap((link) => {
      const accountId = link.attr('data-mention-id') ?? link.attr('data-account-id');
      if (accountId) {
        return [{ accountId, _id: generateId() }];
      }

      const match = matchUserPath(link.attr('href') ?? '');
      if (match) {
        return [{ accountId: match.accountId, _id: generateId() }];
      }

      return [];
    });
  },

  getInitialValue: () => [],

  getInitialDefaultValue: () => USER_VALUE_CURRENT,

  isEmptyValue: ({ value }) =>
    Array.isArray(value) ? value.filter(isNonEmptyUserValue).length === 0 : false,

  isEqualValue: ({ value: value1 }, { value: value2 }) => {
    if (Array.isArray(value1) && Array.isArray(value2)) {
      return isEqual(
        value1.filter(isNonEmptyUserValue).map((v) => v.accountId),
        value2.filter(isNonEmptyUserValue).map((v) => v.accountId)
      );
    } else {
      return value1 === value2;
    }
  },

  getStringRepresentation,

  loadDependencies: ({ value }, { dispatch, selector }) => {
    if (value === USER_VALUE_CURRENT) {
      const currentUserId = selector((state) => selectCurrentUserId(state));
      if (currentUserId) {
        dispatch(loadUserById(currentUserId));
      }
    }

    if (Array.isArray(value)) {
      for (const v of value) {
        if (isNonEmptyUserValue(v)) {
          dispatch(loadUserById(v.accountId));
        }
      }
    }
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: getStringRepresentation,
  },
};

export default userFieldType;
